import { useNavigate } from 'react-router-dom'
import { ArrowLeft, RefreshCw, Plus, FileText, Download, Clock, Filter, Loader2 } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/popover'
import type { DashboardConfigResponse } from '../types'

interface DashboardHeaderProps {
  config: DashboardConfigResponse
  onRefresh: () => void
  isRefreshing?: boolean
  lastUpdated?: Date
  onExportarCompleto?: () => void
  onExportarFiltrado?: () => void
  isExporting?: boolean
  totalRegistros?: number
  totalFiltrados?: number
  hayFiltrosActivos?: boolean
}

export function DashboardHeader({
  config,
  onRefresh,
  isRefreshing,
  lastUpdated,
  onExportarCompleto,
  onExportarFiltrado,
  isExporting,
  totalRegistros,
  totalFiltrados,
  hayFiltrosActivos,
}: DashboardHeaderProps) {
  const navigate = useNavigate()

  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3 min-w-0">
        <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => navigate('/registros/reportes')}>
          <ArrowLeft className="h-3.5 w-3.5" />
        </Button>
        <h1 className="text-lg font-semibold truncate flex items-center gap-1.5">
          <FileText className="h-4 w-4 shrink-0" />
          <span className="truncate">{config.titulo}</span>
        </h1>
        {lastUpdated && (
          <div className="hidden sm:flex items-center gap-1.5 text-xs text-muted-foreground shrink-0">
            <Clock className="h-3 w-3" />
            <span className="font-medium text-foreground">Última actualización:</span>
            <span>
              {lastUpdated.toLocaleDateString('es-EC', { day: '2-digit', month: '2-digit', year: 'numeric' })} {lastUpdated.toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit', hour12: true })}
            </span>
          </div>
        )}
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onRefresh} disabled={isRefreshing}>
          <RefreshCw className={`h-3.5 w-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
        </Button>
        <Button
          size="sm"
          className="h-7 text-xs px-2 bg-teal-800 hover:bg-teal-900 text-white"
          onClick={() => window.open('https://www.medifarmaconecta.com/registro/', '_blank')}
        >
          <Plus className="h-3 w-3 mr-1" />
          Nuevo
        </Button>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs px-2"
              disabled={isExporting}
            >
              {isExporting ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Download className="h-3 w-3 mr-1" />}
              {isExporting ? 'Exportando...' : 'Exportar'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[260px] p-2" align="end">
            <div className="space-y-1">
              <button
                className="w-full flex items-center justify-between gap-2 rounded-md px-3 py-2 text-sm hover:bg-muted transition-colors text-left"
                onClick={onExportarCompleto}
                disabled={isExporting}
              >
                <div className="flex items-center gap-2">
                  <Download className="h-3.5 w-3.5 text-teal-500" />
                  <span>Descarga completa</span>
                </div>
                {totalRegistros != null && (
                  <span className="text-[10px] tabular-nums text-teal-600 dark:text-teal-400 font-semibold shrink-0">
                    {totalRegistros.toLocaleString()}
                  </span>
                )}
              </button>
              <button
                className="w-full flex items-center justify-between gap-2 rounded-md px-3 py-2 text-sm hover:bg-muted transition-colors text-left"
                onClick={onExportarFiltrado}
                disabled={isExporting || !hayFiltrosActivos}
              >
                <div className="flex items-center gap-2">
                  <Filter className="h-3.5 w-3.5 text-teal-500" />
                  <span className={!hayFiltrosActivos ? 'text-muted-foreground' : ''}>
                    Descarga con filtros
                  </span>
                </div>
                {totalFiltrados != null && hayFiltrosActivos ? (
                  <span className="text-[10px] tabular-nums text-teal-600 dark:text-teal-400 font-semibold shrink-0">
                    {totalFiltrados.toLocaleString()}
                  </span>
                ) : (
                  <span className="text-[10px] text-muted-foreground">Sin filtros</span>
                )}
              </button>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  )
}
